import { formatCoachingTime } from '@efdigital/ll-addon-coaching/utils/coaching-sessions/format-coaching-time';
import { scheduledDurationMinutes } from '@efdigital/ll-addon-coaching/utils/coaching-sessions/session-duration';
import { confirmedDurationOptions } from '@efdigital/ll-addon-coaching/utils/coaching-sessions/session-duration';
import type {
  AttendanceConclusion,
  CoachingSession,
} from '@efdigital/ll-addon-data';
import eq from '@efdigital/ll-admin-v2/helpers/eq.ts';
import type AdminSessionsService from '@efdigital/ll-admin-v2/services/admin-sessions';
import {
  outcomeCreditsQuota,
  RESOLVE_OUTCOMES,
  resolvedOutcomeHelp,
  resolvedOutcomeLabel,
} from '@efdigital/ll-admin-v2/utils/coaching/coaching-sessions/resolve-outcomes';
import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import BsModal from 'ember-bootstrap/components/bs-modal';

interface SessionResolveModalSignature {
  Args: {
    coachingSession: CoachingSession;
    onSuccess?: () => Promise<void>;
  };
  Blocks: {
    resolve: [showModal: () => void];
  };
}

export class SessionResolveModal extends Component<SessionResolveModalSignature> {
  @service declare adminSessions: AdminSessionsService;

  @tracked selectedOutcome: AttendanceConclusion | null = null;
  @tracked confirmedDurationMinutes: number | null = null;
  @tracked isSubmitting = false;
  @tracked visible = false;

  get scheduledMinutes(): number {
    return scheduledDurationMinutes(this.args.coachingSession);
  }

  get durationOptions(): number[] {
    return confirmedDurationOptions(this.scheduledMinutes);
  }

  get isCoachingSession(): boolean {
    return this.args.coachingSession.sessionType === 'coaching';
  }

  get quotaCreditMinutes(): number {
    return this.isCoachingSession ? this.scheduledMinutes : 1;
  }

  openModal = () => {
    this.selectedOutcome = null;
    this.confirmedDurationMinutes = this.scheduledMinutes;
    this.visible = true;
  };

  closeModal = () => {
    this.visible = false;
  };

  get disableResolveButton() {
    if (!this.selectedOutcome || this.isSubmitting) {
      return true;
    }

    if (
      this.selectedOutcome === 'attended' &&
      this.isCoachingSession &&
      !this.confirmedDurationMinutes
    ) {
      return true;
    }

    return false;
  }

  get confirmOutcomeLabel() {
    return this.selectedOutcome
      ? resolvedOutcomeLabel(this.selectedOutcome)
      : '';
  }

  selectOutcome = (outcome: AttendanceConclusion) => {
    this.selectedOutcome = outcome;
  };

  setConfirmedDuration = (event: Event) => {
    const value = parseInt((event.target as HTMLSelectElement).value, 10);
    this.confirmedDurationMinutes = Number.isFinite(value) ? value : null;
  };

  submit = async (event: SubmitEvent) => {
    event.preventDefault();

    const outcome = this.selectedOutcome;
    if (!outcome || this.isSubmitting) {
      return;
    }

    this.isSubmitting = true;

    try {
      await this.adminSessions.resolveSession(this.args.coachingSession.id!, {
        resolvedOutcome: outcome,
        ...(outcome === 'attended' &&
        this.isCoachingSession &&
        this.confirmedDurationMinutes
          ? { confirmedDurationMinutes: this.confirmedDurationMinutes }
          : {}),
      });
      await this.args.onSuccess?.();
      this.closeModal();
    } finally {
      this.isSubmitting = false;
    }
  };

  <template>
    {{yield this.openModal to="resolve"}}
    <BsModal
      @open={{this.visible}}
      @onHidden={{this.closeModal}}
      @size="lg"
      @renderInPlace={{true}}
      @backdropClose={{false}}
      as |modal|
    >
      <modal.header>
        <h2 class="modal-title h5">Resolve attendance</h2>
      </modal.header>
      <modal.body>
        <form {{on "submit" this.submit}} data-test-session-resolve-form>
          <p class="text-body-secondary mb-3">
            Settle this attendance dispute with an admin-chosen outcome. Quota
            and finance adjustments will be applied automatically.
          </p>

          <fieldset class="mb-3">
            <legend class="gud-eyebrow">Resolution</legend>
            {{#each RESOLVE_OUTCOMES as |outcome|}}
              <div class="form-check mb-2">
                <input
                  class="form-check-input"
                  type="radio"
                  name="resolvedOutcome"
                  id="resolved-outcome-{{outcome}}"
                  checked={{eq this.selectedOutcome outcome}}
                  disabled={{this.isSubmitting}}
                  data-test-resolve-outcome={{outcome}}
                  {{on "change" (fn this.selectOutcome outcome)}}
                />
                <label
                  class="form-check-label"
                  for="resolved-outcome-{{outcome}}"
                >
                  <span class="fw-semibold">{{resolvedOutcomeLabel
                      outcome
                    }}</span>
                  <div class="form-text mb-0 mt-0 lh-sm">{{resolvedOutcomeHelp
                      outcome
                    }}</div>
                  {{#if (outcomeCreditsQuota outcome)}}
                    <div class="form-text mb-0 lh-sm">
                      <span class="badge rounded-5 text-bg-success">
                        +{{if
                          this.isCoachingSession
                          (formatCoachingTime this.quotaCreditMinutes)
                          "1"
                        }}
                      </span>
                      Credits quota to the plan.
                    </div>
                  {{/if}}
                </label>
              </div>
            {{/each}}
          </fieldset>

          {{#if (eq this.selectedOutcome "attended")}}
            {{#if this.isCoachingSession}}
              <div class="mb-3">
                <label
                  class="form-label"
                  for="resolve-confirmed-duration"
                >Confirmed duration</label>
                <select
                  class="form-select"
                  id="resolve-confirmed-duration"
                  disabled={{this.isSubmitting}}
                  data-test-resolve-confirmed-duration
                  {{on "change" this.setConfirmedDuration}}
                >
                  {{#each this.durationOptions as |mins|}}
                    <option
                      value={{mins}}
                      selected={{eq this.confirmedDurationMinutes mins}}
                    >{{mins}} min</option>
                  {{/each}}
                </select>
                <div class="form-text">Scheduled:
                  {{this.scheduledMinutes}}
                  min</div>
              </div>
            {{/if}}
          {{/if}}

          <button
            type="submit"
            class="btn btn-primary"
            disabled={{this.disableResolveButton}}
            data-test-resolve-open-confirm
          >
            {{if this.isSubmitting "Resolving…" "Resolve attendance"}}
          </button>
        </form>
      </modal.body>
    </BsModal>
  </template>
}
